const frozen: unique symbol = Symbol("frozen map");

/** Immutable map type. Branded to indicate that a particular instance is immutable forevermore. */
export interface StrictlyFrozenMap<X, Y> extends Readonly<Map<X, Y>> {
    [frozen]: true;
}

/**
 * Freeze a map, making it immutable.
 * @param map The original map.
 * @returns The same map reference, after freezing the map.
 */
export function mapFreeze<X, Y>(map: Map<X, Y>) {
    if (isFrozenMap<X, Y>(map)) {
        return map;
    }

    mapFreezeMethod(map, "set");
    mapFreezeMethod(map, "delete");
    mapFreezeMethod(map, "clear");
    Object.defineProperty(map, frozen, { value: true });

    return Object.freeze(map) as StrictlyFrozenMap<X, Y>;
}

function isFrozenMap<X, Y>(map: unknown): map is StrictlyFrozenMap<X, Y> {
    return typeof map === "object" && !!map && frozen in map && !!map[frozen];
}

function mapFreezeMethod<X, Y>(map: Map<X, Y>, key: keyof Map<X, Y>) {
    Object.defineProperty(map, key, {
        value: () => {
            throw new Error("A frozen map cannot be modified.");
        },
    });
}

/**
 * Determine whether two maps have the same key-value pairs, ignoring insertion order.
 * @param first First of the two maps being compared.
 * @param second Second of the two maps being compared.
 * @returns Boolean indicating whether or not the maps are equal.
 * Values are compared using strict equality (as if the === operator were used).
 */
export function mapEquals<X, Y>(first: Map<X, Y>, second: Map<X, Y>) {
    if (first === second) {
        return true;
    }

    if (second.size !== first.size) {
        return false;
    }

    return isSubmap(first, second);
}

function isSubmap<X, Y>(candidate: Map<X, Y>, of: Map<X, Y>) {
    for (const item of candidate) {
        if (!of.has(item[0]) || of.get(item[0]) !== item[1] /* values compared using strict equality */) {
            return false;
        }
    }
    return true;
}
